import React, { useMemo } from 'react';
import { useAuthContext } from 'contexts';
import { config } from 'config';
import { useGetProfileSidebarUserOfficeAccessQuery } from 'generated/graphql';
import { useProfilePageUserContext } from '../ProfilePageUserContext';
import { ProfilePageUserRole } from 'hooks/useProfilePageUser';
import {
  PermissionsCard,
  PermissionsCardItem,
  PermissionsCardSkeleton,
} from './PermissionsCard';

type PermissionsCardContainerProps = {
  userId: string;
};

const PLACES_MANAGE_PERMISSION = 'places:manage';

/**
 * Office-admin roles are auto-generated per location and have no settings
 * detail page, so they are never linked (mirrors the legacy profile page's
 * getIsOfficeAdminRole).
 */
const isOfficeAdminRole = (role: ProfilePageUserRole): boolean =>
  !!(
    role.slug?.match(/^admin_location_\d+?$/) &&
    role.name.match(/^office\sadmin\s(?:for\s)?\d+?$/i)
  );

export const PermissionsCardContainer = ({
  userId,
}: PermissionsCardContainerProps): JSX.Element | null => {
  const { currentUser, userPermissions, orgSlug } = useAuthContext();
  const isCurrentPerson = currentUser?.id === userId;

  // Visibility mirrors the legacy profile page: passes are shown to the user
  // themselves or to viewers who can view office access; administration,
  // groups and roles require the viewer to manage places.
  const canViewOfficeAccess = userPermissions?.canViewOfficeAccess ?? false;
  const canManagePlaces = userPermissions?.canManagePlaces ?? false;
  const showPasses = isCurrentPerson || canViewOfficeAccess;
  const showOfficeAdmin = canManagePlaces;
  const showCard = showPasses || showOfficeAdmin;

  const { user, managedLocationNames, loading } = useProfilePageUserContext();

  const { data: officeAccessData, loading: passesLoading } =
    useGetProfileSidebarUserOfficeAccessQuery({
      variables: { userId },
      skip: !showCard || !showPasses,
      errorPolicy: 'all',
    });

  const officeAdministration = useMemo<PermissionsCardItem[]>(() => {
    if (!user) {
      return [];
    }
    const hasOrgWideManage =
      user.permissions?.[PLACES_MANAGE_PERMISSION] === true;
    if (hasOrgWideManage) {
      return [{ key: user.account.id, label: user.account.name }];
    }
    return managedLocationNames.map((name, index) => ({
      key: `${index}-${name}`,
      label: name,
    }));
  }, [user, managedLocationNames]);

  const passes = useMemo<PermissionsCardItem[]>(() => {
    const access = officeAccessData?.getUserById?.officeAccess ?? [];
    return access.map((pass, index) => {
      const placeName = pass.location?.name ?? pass.campus?.name ?? '';
      const label = placeName ? `${pass.name} · ${placeName}` : pass.name ?? '';
      return { key: pass.slug ?? `${index}-${label}`, label };
    });
  }, [officeAccessData]);

  // Tags link to the legacy dashboard's settings detail pages for viewers who
  // can manage groups/roles (the legacy profile page links them for admins).
  const canManageGroups = userPermissions?.canManageGroups ?? false;
  const canManageRoles = userPermissions?.canManageRoles ?? false;

  const groups = useMemo<PermissionsCardItem[]>(
    () =>
      (user?.groups ?? []).map((group) => ({
        key: group.slug || String(group.id),
        label: group.name,
        href:
          canManageGroups && orgSlug && group.slug
            ? `${config.dashboardUrl}/${orgSlug}/settings/groups/${group.slug}`
            : undefined,
      })),
    [user, canManageGroups, orgSlug]
  );

  const roles = useMemo<PermissionsCardItem[]>(
    () =>
      (user?.roles ?? []).map((role, index) => ({
        key: role.slug || role.id?.toString() || `${index}-${role.name}`,
        label: role.name,
        href:
          canManageRoles && orgSlug && role.slug && !isOfficeAdminRole(role)
            ? `${config.dashboardUrl}/${orgSlug}/settings/roles/${role.slug}`
            : undefined,
      })),
    [user, canManageRoles, orgSlug]
  );

  if (!showCard) {
    return null;
  }

  if (loading && !user) {
    return <PermissionsCardSkeleton />;
  }

  // The card is supplementary; if the org-user read failed entirely there is
  // nothing meaningful to show, so hide it rather than rendering an error.
  if (!user) {
    return null;
  }

  return (
    <PermissionsCard
      showOfficeAdmin={showOfficeAdmin}
      showPasses={showPasses}
      officeAdministration={officeAdministration}
      passes={passes}
      groups={groups}
      roles={roles}
      passesLoading={passesLoading}
    />
  );
};
